// Reusable dialog for viewing test questions and downloading worksheet PDF.
// Used by List of Tests (TestsPage) and Student Assignments (StudentAssignmentsPage).

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import testService from '../../services/testService';
import MathRenderer from '../common/MathRenderer';

const VIEW_QUESTION_COLUMNS = (function getColumns() {
  return [
    { field: 'id', headerName: 'ID', width: 70, align: 'center', headerAlign: 'center' },
    {
      field: 'question',
      headerName: 'Question',
      flex: 1,
      minWidth: 400,
      renderCell: (params) => (
        <Box sx={{ py: 1, width: '100%', maxWidth: '100%' }}>
          <MathRenderer
            content={params.row.question_text || params.row.question || params.row.text || 'No question text'}
            variant="body2"
            sx={{ wordBreak: 'break-word', lineHeight: 1.5 }}
          />
          {params.row.image_url && (
            <Box sx={{ mt: 1, mb: 2, maxWidth: '100%', maxHeight: 200, overflow: 'hidden' }}>
              <img
                src={params.row.image_proxy_url || params.row.image_url}
                alt="Question"
                style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: 4 }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </Box>
          )}
          {['MULTIPLE', 'MCQ', 'MULTIPLE_CHOICE'].includes(params.row.question_type) && params.row.options && !params.row.has_subquestions && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>Options:</Typography>
              <Box sx={{ pl: 1 }}>
                {Object.entries(params.row.options).map(([key, value], index) => {
                  const correctKey = params.row.correct_answer ?? params.row.correct_option ?? params.row.correct_option_key ?? params.row.answer ?? params.row.correct ?? null;
                  const keyExists = Object.prototype.hasOwnProperty.call(params.row.options, correctKey);
                  const isCorrect = keyExists ? (String(key) === String(correctKey)) : (String(value) === String(correctKey));
                  return (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, p: 1, borderRadius: 1, bgcolor: isCorrect ? 'success.light' : 'transparent' }}>
                      <Typography component="span" sx={{ fontWeight: 'bold', minWidth: '24px', color: isCorrect ? 'success.dark' : 'text.primary' }}>{index + 1}.</Typography>
                      <MathRenderer content={value} variant="body2" sx={{ ml: 1, color: isCorrect ? 'success.dark' : 'text.primary', wordBreak: 'break-word' }} />
                      {isCorrect && <CheckCircleOutlineIcon fontSize="small" color="success" sx={{ ml: 1, mt: '2px' }} />}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
          {params.row.has_subquestions && Array.isArray(params.row.sub_questions) && params.row.sub_questions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>Sub-questions:</Typography>
              <Box sx={{ pl: 2.5, m: 0, listStyle: 'none' }}>
                {/* Sort sub-questions by order field to maintain test sequence */}
                {[...params.row.sub_questions].sort((a, b) => {
                  const orderA = a.order ?? a.order_number ?? 0;
                  const orderB = b.order ?? b.order_number ?? 0;
                  if (orderA !== orderB) return orderA - orderB;
                  // Fallback to id if order is same
                  return (a.id ?? 0) - (b.id ?? 0);
                }).map((subQ) => (
                  <Box key={subQ.id} component="div" sx={{ mb: 1.5 }}>
                    <MathRenderer
                      content={(subQ.letter ? `${subQ.letter}. ` : '') + ((subQ.question_text || subQ.text || '').trim() || '—')}
                      variant="body2"
                      sx={{ wordBreak: 'break-word' }}
                    />
                    {subQ.options && (Array.isArray(subQ.options) ? subQ.options.length > 0 : Object.keys(subQ.options || {}).length > 0) && (
                      <Box sx={{ pl: 1, mt: 0.5 }}>
                        {(Array.isArray(subQ.options) ? subQ.options : Object.entries(subQ.options || {})).map((opt, oidx) => {
                          const optVal = Array.isArray(subQ.options) ? opt : opt[1];
                          const correctKey = subQ.correct_answer ?? subQ.correct_option;
                          const isCorrect = correctKey != null && (String(optVal) === String(correctKey) || (typeof opt === 'object' && String(opt[0]) === String(correctKey)));
                          return (
                            <Box key={oidx} component="div" sx={{ display: 'flex', alignItems: 'flex-start', color: isCorrect ? 'success.dark' : 'text.secondary' }}>
                              <Typography component="span" variant="body2">{String.fromCharCode(97 + oidx)}. </Typography>
                              <MathRenderer content={String(optVal)} variant="body2" sx={{ flex: 1 }} />
                              {isCorrect && <CheckCircleOutlineIcon fontSize="small" color="success" sx={{ ml: 0.5, mt: '2px' }} />}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )
    },
    {
      field: 'difficulty',
      headerName: 'Difficulty',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const difficulty = params.row.difficulty || params.row.level;
        return (
          <Chip
            label={difficulty}
            size="small"
            color={difficulty === 'Basic' ? 'primary' : difficulty === 'Intermediate' ? 'secondary' : 'default'}
            variant="outlined"
            sx={{ textTransform: 'capitalize', minWidth: 90, justifyContent: 'center' }}
          />
        );
      }
    }
  ];
})();

export { VIEW_QUESTION_COLUMNS };
export default function ViewTestQuestionsDialog({ open, onClose, test, questions = [], onMessage }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadWorksheet = async () => {
    if (!test?.id) return;
    try {
      setDownloading(true);
      await testService.downloadTestWorksheet(test.id, test.title || 'worksheet');
      onMessage?.('Worksheet downloaded successfully!', 'success');
    } catch (error) {
      if (error.response) {
        onMessage?.(`Failed to download worksheet: ${error.response.status} ${error.response.statusText}`, 'error');
      } else {
        onMessage?.(error.message || 'Error downloading worksheet. Please try again.', 'error');
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          minHeight: { xs: '90vh', sm: '80vh' },
          maxHeight: { xs: '95vh', sm: '90vh' },
          width: { xs: '98vw', sm: '95vw', md: '90vw' },
          maxWidth: '1400px',
          margin: { xs: '8px', sm: '16px' }
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }}>
            Questions for: {test?.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {questions.length} question{questions.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{
          height: { xs: 'calc(90vh - 140px)', sm: 'calc(80vh - 140px)' },
          overflow: 'auto',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: '#f1f1f1' },
          '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#a8a8a8' }
        }}>
          <DataGrid
            rows={questions}
            columns={VIEW_QUESTION_COLUMNS}
            autoHeight={false}
            hideFooter
            getRowHeight={() => 'auto'}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell': { borderBottom: '1px solid', borderColor: 'divider', padding: '16px 8px' },
              '& .MuiDataGrid-columnHeaders': { backgroundColor: 'background.paper', borderBottom: '2px solid', borderColor: 'divider' },
              '& .MuiDataGrid-row': {
                '&:nth-of-type(even)': { backgroundColor: 'grey.50' },
                '&:hover': { backgroundColor: 'action.hover' }
              },
              '& .MuiDataGrid-cell:focus': { outline: 'none' }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        {test?.id && (
          <Button
            onClick={handleDownloadWorksheet}
            variant="outlined"
            color="secondary"
            startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            disabled={downloading}
          >
            {downloading ? 'Downloading...' : 'Download Worksheet'}
          </Button>
        )}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
